<template>
    <div class="autoprofile" :id="id">
        <div class="image-upload">
            <h5>{{ t("imageUploadTitle") }}</h5>
            <input
                ref="file"
                type="file"
                style="display: none"
                accept="image/*"
                multiple="false"
                @change="selectFile"
            />
            <div class="image-preview-wrapper" @click="$refs.file.click()">
                <div
                    v-if="!selectedFiles && !loading"
                    class="image-preview-placeholder"
                >
                    <img
                        src="@/assets/customizer_icon_upload.svg"
                        width="24"
                        height="24"
                    />
                    {{ t("addImageOptional") }}
                </div>

                <LoadingIndicator v-if="loading" />

                <img
                    v-if="selectedFiles && !loading && imagePreview"
                    :src="imagePreview"
                    :alt="t('imagePreviewAlt')"
                />
            </div>
            <p
                @click="openImageUpload()"
                style="
                    font-size: 14px;
                    text-decoration: underline;
                    cursor: pointer;
                "
            >
                {{ t("howItWorks") }}
            </p>
        </div>
        <div class="ai-form">
            <h5>{{ t("fillManually") }}</h5>
            <div class="ai-form-row">
                <div class="ai-input-wrapper">
                    <h5>{{ t("eyeColor") }}</h5>
                    <DTColorRadio
                        ref="eyeColor"
                        :payload="eyeColorPayload"
                        :sharePayload="sharePayloads"
                        :options="eyeColorOptions"
                        :required="eyeColorRequired"
                    />
                </div>
                <div class="ai-input-wrapper">
                    <h5>{{ t("skinColor") }}</h5>
                    <DTColorRadio
                        ref="skinColor"
                        :payload="skinColorPayload"
                        :sharePayload="sharePayloads"
                        :options="skinColorOptions"
                        :required="skinColorRequired"
                    />
                </div>
            </div>
            <div class="ai-form-row">
                <div class="ai-input-wrapper">
                    <h5>{{ t("hairstyle") }}</h5>
                    <DTInput
                        ref="hairstyle"
                        type="text"
                        :autoHeight="true"
                        :payload="hairstylePayload"
                        :sharePayload="sharePayloads"
                        :placeholder="t('hairstylePlaceholder')"
                        :required="hairstyleRequired"
                    />
                </div>
            </div>
            <div class="ai-form-row">
                <div class="ai-input-wrapper">
                    <h5>{{ t("specialFeatures") }}</h5>
                    <DTInput
                        ref="misc"
                        type="text"
                        :autoHeight="true"
                        :payload="miscPayload"
                        :sharePayload="sharePayloads"
                        :placeholder="t('specialFeaturesPlaceholder')"
                        :required="miscRequired"
                        :scrollToOnFocus="true"
                    />
                </div>
            </div>
        </div>
    </div>
    <PopUp id="autoprofile-popup" ref="AutoProfileAIPopup" maxWidth="480px">
        <h3>{{ t("imageUploadTitle") }}</h3>
        <p style="margin-top: 32px">
            {{ t("aiIntro") }} {{ t("recommendationsTitle") }}
        </p>
        <ul style="padding-left: 25px">
            <li v-for="recommendation in recommendationsList" :key="recommendation">
                {{ recommendation }}
            </li>
        </ul>
        <p style="margin-top: 16px; color: #b55e2b">
            {{ t("privacy") }}
        </p>
        <DTButton
            @click="
                $refs.file.click();
                $refs.AutoProfileAIPopup.closePopup();
            "
            type="primary"
            :text="t('uploadImage')"
            style="margin-top: 32px; width: 100%"
        />
    </PopUp>
    <BottomSheet
        id="autoprofile-bottomsheet"
        ref="AutoProfileAIBottomsheet"
        :title="t('imageUploadTitle')"
    >
        <p style="margin: 24px 0 0">
            {{ t("aiIntro") }} {{ t("recommendationsTitle") }}
        </p>
        <ul style="padding-left: 25px; margin: 0">
            <li v-for="recommendation in recommendationsList" :key="recommendation">
                {{ recommendation }}
            </li>
        </ul>
        <p style="margin: 18px 0 0; color: #b55e2b">
            {{ t("privacy") }}
        </p>
        <DTButton
            @click="
                $refs.AutoProfileAIBottomsheet.closeBottomsheet();
                $refs.AutoProfileAIBottomsheetSelectOption.openBottomsheet();
            "
            type="primary"
            :text="t('uploadImage')"
            style="margin: 24px 0 0; width: 100%"
        />
    </BottomSheet>
    <BottomSheet
        id="autoprofile-bottomsheet-select-option"
        ref="AutoProfileAIBottomsheetSelectOption"
        :title="t('imageUploadTitle')"
    >
        <a
            @click="
                $refs.AutoProfileAIBottomsheetSelectOption.closeBottomsheet();
                $refs.file.removeAttribute('capture', 'camera');
                $refs.file.click();
            "
            href="#/"
            class="bottomsheet-select-option-mobile"
        >
            <img
                src="@/assets/customizer_icon_media.svg"
                width="24"
                height="24"
            />
            {{ t("mediaLibrary") }}
        </a>
        <a
            @click="
                $refs.AutoProfileAIBottomsheetSelectOption.closeBottomsheet();
                $refs.file.setAttribute('capture', 'camera');
                $refs.file.click();
            "
            href="#/"
            class="bottomsheet-select-option-mobile"
        >
            <img
                src="@/assets/customizer_icon_camera.svg"
                width="24"
                height="24"
            />
            {{ t("openCamera") }}
        </a>
    </BottomSheet>
</template>

<script>
import DTColorRadio from "./DTColorRadio.vue";
import DTInput from "./DTInput.vue";
import LoadingIndicator from "./LoadingIndicator.vue";
import axios from "axios";
import PopUp from "./PopUp.vue";
import DTButton from "./DTButton.vue";
import BottomSheet from "./BottomSheet.vue";
import { customizerText } from "../utils/customizerTexts";

export default {
    name: "AutoProfileAI",
    components: {
        DTColorRadio,
        DTInput,
        LoadingIndicator,
        PopUp,
        DTButton,
        BottomSheet,
    },
    props: {
        id: {
            type: String,
            default: "",
        },
        sharePayloads: {
            type: Boolean,
            default: false,
        },
        eyeColorOptions: {
            type: Array,
            required: true,
            default: () => [],
        },
        eyeColorPayload: {
            type: String,
            required: true,
        },
        eyeColorRequired: {
            type: Boolean,
            default: false,
        },
        skinColorOptions: {
            type: Array,
            required: true,
            default: () => [],
        },
        skinColorPayload: {
            type: String,
            required: true,
        },
        skinColorRequired: {
            type: Boolean,
            default: false,
        },
        hairstylePayload: {
            type: String,
            required: true,
        },
        hairstyleRequired: {
            type: Boolean,
            default: false,
        },
        miscPayload: {
            type: String,
            required: true,
        },
        miscRequired: {
            type: Boolean,
            default: false,
        },
        requestUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            selectedFiles: undefined,
            loading: false,
            imagePreview: undefined,
        };
    },
    computed: {
        recommendationsList() {
            return customizerText("recommendations").split(";");
        },
    },
    methods: {
        t(key) {
            return customizerText(key);
        },
        selectFile() {
            if (this.$refs.file && this.$refs.file.files.length > 0) {
                this.selectedFiles = this.$refs.file.files;
            } else {
                this.selectedFiles = undefined;
            }
        },
        openImageUpload() {
            if (window.innerWidth > 991) {
                this.$refs.AutoProfileAIPopup.openPopup();
            } else {
                this.$refs.AutoProfileAIBottomsheet.openBottomsheet();
            }
        },
    },
    watch: {
        selectedFiles() {
            if (!this.selectedFiles) return;

            this.loading = true;
            const formData = new FormData();
            formData.append("file", this.selectedFiles[0]);

            axios
                .post(this.requestUrl, formData, {
                    customizerFeature: "photo_upload",
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((response) => {
                    this.$refs.file.removeAttribute("capture");

                    this.imagePreview = URL.createObjectURL(
                        this.selectedFiles[0]
                    );

                    const data = response?.data;

                    if (data?.eyecolor) {
                        this.$refs.eyeColor.setAIValue(data.eyecolor);
                    }

                    if (data?.skincolor) {
                        this.$refs.skinColor.setAIValue(data.skincolor);
                    }

                    if (data?.hairstyle) {
                        this.$refs.hairstyle.setAIValue(data.hairstyle);
                    }

                    if (data?.misc) {
                        this.$refs.misc.setAIValue(data.misc);
                    }
                })
                .catch((error) => {
                    // sentry-ignore: the failed upload is reported by installAxiosReporting (feature photo_upload)
                    if (process.env.VUE_APP_DEV_MODE === "true") {
                        console.error("[AutoProfileAI] Error during image upload", {
                            code: error?.code || "unknown",
                            status: error?.response?.status || "none",
                        });
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;
.autoprofile {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 40px;
    width: 100%;

    @media (max-width: $breakpoint-tablet) {
        flex-direction: column;
        row-gap: 32px;
    }

    .image-upload {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        row-gap: 16px;
        width: 100%;
        max-width: 142px;

        .image-preview-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 142px;
            border-radius: 16px;
            border: 2px solid $color-new-black;
            cursor: pointer;
            overflow: hidden;

            .image-preview-placeholder {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                row-gap: 12px;
                width: 100%;
                padding: 10px;
                text-align: center;
                font-weight: 500;
                color: #6f6c90;
            }

            > img {
                width: 100%;
                aspect-ratio: 1;
                object-fit: cover;
                object-position: center;
            }
        }
    }

    .ai-form {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        row-gap: 32px;
        width: 100%;

        .ai-form-row {
            display: flex;
            flex-wrap: nowrap;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            column-gap: 16px;
            width: 100%;

            @media (max-width: $breakpoint-mobile) {
                flex-direction: column;
                row-gap: 32px;
            }

            .ai-input-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: stretch;
                row-gap: 16px;
                width: 100%;
            }
        }
    }
}

#autoprofile-bottomsheet-select-option {
    .bottomsheet-select-option-mobile {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        column-gap: 16px;
        margin-top: 32px;
        text-decoration: none;
        color: $color-new-black;
        cursor: pointer;

        &:hover {
            color: $color-new-black;
        }

        img {
            width: 24px;
            height: 24px;
        }
    }
}
</style>
